import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastProviderRadix, ToastViewport, ToastItem } from '../components/ui/Toast';
import { useLanguage } from './LanguageContext';

// LOT URGENT P0 FE-1 — contexte Toast global. API étendue Toast Lot 1 :
// showError / showSuccess / showWarning / showInfo, signature uniforme
// (message, options?) où options = { title?, duration? }. title optionnel
// → fallback i18n par variant (common.toast.{variant}Title). Provider monté
// dans index.js sous LanguageProvider donc useLanguage utilisable ici.

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

let _toastIdCounter = 0;

const FALLBACK_TITLE_KEY = {
  error: 'common.toast.errorTitle',
  success: 'common.toast.successTitle',
  warning: 'common.toast.warningTitle',
  info: 'common.toast.infoTitle',
};

export const ToastProvider = ({ children }) => {
  const { t } = useLanguage();
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Helper interne : pousse un toast dans le state avec fallback title via i18n.
  // Exposé indirectement via showError/showSuccess/showWarning/showInfo.
  const push = useCallback((variant, message, options) => {
    _toastIdCounter += 1;
    const id = _toastIdCounter;
    const resolvedTitle = options?.title ?? t(FALLBACK_TITLE_KEY[variant]);
    setToasts((prev) => [
      ...prev,
      { id, variant, title: resolvedTitle, message, duration: options?.duration },
    ]);
  }, [t]);

  const showError = useCallback((message, options) => push('error', message, options), [push]);
  const showSuccess = useCallback((message, options) => push('success', message, options), [push]);
  const showWarning = useCallback((message, options) => push('warning', message, options), [push]);
  const showInfo = useCallback((message, options) => push('info', message, options), [push]);

  return (
    <ToastContext.Provider value={{ showError, showSuccess, showWarning, showInfo }}>
      <ToastProviderRadix swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            variant={toast.variant}
            title={toast.title}
            description={toast.message}
            duration={toast.duration}
            open
            onOpenChange={(isOpen) => {
              if (!isOpen) removeToast(toast.id);
            }}
          />
        ))}
        <ToastViewport />
      </ToastProviderRadix>
    </ToastContext.Provider>
  );
};
